/// Libraries
import { err, ok, Result } from "neverthrow";
/// Vuex Types
import { KtznEventSubscriptionAction } from "@/framework/store/ktznEventSubscription/types";
import { AuthMutation } from "@/framework/store/auth/types"; // TODO: Replace with actions or remove if not needed
import { ConversationMutation } from "@/framework/store/conversation/types";
import { ComponentMutation } from "@/framework/store/component/types"; /// Service
import KtznEventSubscriptionRepository, {
  IKtznEventSubscriptionRepository,
} from "@/framework/repositories/ktznEventSubscriptionRepository";
import { KtznEventSubscription } from "@/framework/repositories/ktznEventSubscriptionCache";
import { CommonVuexStateRepository } from "@/framework/repositories/CommonVuexStateRepository";
import { UserService } from "@/modules/auth/services/UserService"; /* eslint-disable no-case-declarations */
import KtznEventSubscriptionError from "@/framework/errors/websocket/ktznEventSubscriptionError";
import StoreSubscriptionServiceError from "@/framework/errors/storeSubscriptionServiceError";

/* eslint-disable no-case-declarations */

export interface IStoreSubscriptionService {
  /**
   * Handles the vuex subscribe action.
   * @param action Vuex store action
   * @param state Vuex store state
   * @returns A successful `ok` result with value `null` or an StoreSubscriptionServiceError.
   */
  handleSubscribeAction(action: any, state?: any): Promise<Result<null, StoreSubscriptionServiceError>>;

  /**
   * Handles the Vuex store subscribe mutation.
   * @param mutation Vuex store mutation
   * @param state Vuex store state
   * @param store Vuex store object
   */
  handleSubscribeMutation(mutation: any, state: any, store: any): Promise<void>;
}

/**
 * Handles the vuex store related `action` and `mutation` events.
 *
 * It's currently used to handle the vuex store received
 * Kotozna WebSocket and subscription events and forwards them to the `KtznEventSubscriptionRepository`.
 * The `KtznEventSubscriptionRepository` itself handles all the Kotozna websocket event subscription logic.
 */
export class StoreSubscriptionService implements IStoreSubscriptionService {
  private _ktznEventSubscriptionRepository: IKtznEventSubscriptionRepository;
  private _commonVuexStateRepository: CommonVuexStateRepository;

  constructor(
    ktznEventSubscriptionRepository: IKtznEventSubscriptionRepository = KtznEventSubscriptionRepository,
    commonVuexStateRepository: CommonVuexStateRepository = new CommonVuexStateRepository()
  ) {
    this._ktznEventSubscriptionRepository = ktznEventSubscriptionRepository;
    this._commonVuexStateRepository = commonVuexStateRepository;
  }

  /**
   * Handles the vuex subscribe action.
   * @param action Vuex store action
   * @param state Vuex store state
   * @returns A successful `ok` result with value `null` or an StoreSubscriptionServiceError.
   */
  handleSubscribeAction = async (action: any, state: any): Promise<Result<null, StoreSubscriptionServiceError>> => {
    let res: Result<KtznEventSubscription | KtznEventSubscription[] | null[] | null, KtznEventSubscriptionError>;
    switch (action.type) {
      case KtznEventSubscriptionAction.startWebsocketConnection:
        console.log("KtznEventSubscriptionAction.startWebsocketConnection called");
        const authRes = await new UserService().getUserAccessId();
        if (authRes.isOk()) {
          const userAccessId = authRes.value;
          if (userAccessId) {
            res = await this._ktznEventSubscriptionRepository.start(
              userAccessId,
              this._commonVuexStateRepository.getSubscriptionEndpoint()
            );
            if (res && res.isOk()) return ok(null);
            else return err(new StoreSubscriptionServiceError(StoreSubscriptionServiceError.Type.startFailed));
          } else {
            return err(new StoreSubscriptionServiceError(StoreSubscriptionServiceError.Type.noUserAccessId));
          }
        } else {
          return err(new StoreSubscriptionServiceError(StoreSubscriptionServiceError.Type.authFailed, authRes.error));
        }
      case KtznEventSubscriptionAction.subscribeToConversation:
        res = await this._ktznEventSubscriptionRepository.subscribeToConversation(action.payload as string);
        if (res && res.isOk()) return ok(null);
        else
          return err(
            new StoreSubscriptionServiceError(StoreSubscriptionServiceError.Type.subscribeToConversationFailed)
          );
      case KtznEventSubscriptionAction.subscribeToConversations:
        res = await this._ktznEventSubscriptionRepository.subscribeToConversations(action.payload as string[]);
        if (res && res.isOk()) return ok(null);
        else
          return err(
            new StoreSubscriptionServiceError(StoreSubscriptionServiceError.Type.subscribeToConversationsFailed)
          );
      case KtznEventSubscriptionAction.subscribeToTenant:
        res = await this._ktznEventSubscriptionRepository.subscribeToTenant(action.payload as number);
        if (res && res.isOk()) return ok(null);
        else return err(new StoreSubscriptionServiceError(StoreSubscriptionServiceError.Type.subscribeToTenantFailed));
      case KtznEventSubscriptionAction.subscribeToUser:
        res = await this._ktznEventSubscriptionRepository.subscribeToUser(action.payload as string);
        if (res && res.isOk()) return ok(null);
        else return err(new StoreSubscriptionServiceError(StoreSubscriptionServiceError.Type.subscribeToUserFailed));
      case KtznEventSubscriptionAction.unsubscribeFromAllEvents:
        res = await this._ktznEventSubscriptionRepository.unsubscribeFromAllEvents();
        if (res && res.isOk()) return ok(null);
        else
          return err(
            new StoreSubscriptionServiceError(StoreSubscriptionServiceError.Type.unsubscribeFromAllEventsFailed)
          );
      case KtznEventSubscriptionAction.stopWebsocketConnection:
        res = await this._ktznEventSubscriptionRepository.stop();
        if (res && res.isOk()) return ok(null);
        else return err(new StoreSubscriptionServiceError(StoreSubscriptionServiceError.Type.stopFailed));
      default:
        // Return ok for unknown events
        return ok(null);
    }
  };

  /**
   * Handles the Vuex store subscribe mutation.
   * @param mutation Vuex store mutation
   * @param state Vuex store state
   * @param store Vuex store object
   * @returns
   */
  handleSubscribeMutation = async (mutation: any, state: any, store: any): Promise<void> => {
    if (mutation.type === AuthMutation.LOGOUT.toString()) {
      console.log("Logout: all subscription canceled");
      const res = await this._ktznEventSubscriptionRepository.unsubscribeFromAllEvents();
      if (res && res.isErr()) console.log("VuexMutation.unsubscribeFromAllEvents err: ", res.error.type);
      return;
    }

    /// This still needs to be refactored. Migrated for compatibility reasons.

    if (
      mutation.type === ConversationMutation.SET_CURRENT_CONVERSATION_ID &&
      state.conversation.currentConversationId
    ) {
      this._ktznEventSubscriptionRepository.subscribeToEvent(
        `conversation/${state.conversation.currentConversationId}`
      );
    }

    if (
      mutation.type === ConversationMutation.SET_CURRENT_CONVERSATION_ID_WITH_STAFF &&
      state.conversation.currentConversationIdWithStaff
    ) {
      this._ktznEventSubscriptionRepository.subscribeToEvent(
        `conversation/${state.conversation.currentConversationIdWithStaff}`
      );
    }

    if (
      mutation.type === ComponentMutation.SET_NAVIGATION &&
      !state.conversation.currentConversationId &&
      window.location.pathname.endsWith("conversations")
    ) {
      // チャット一覧画面に戻っている場合、conversation一覧をunsubscribe
      // If you are back in the chat list screen, unsubscribe the conversation list
      this._ktznEventSubscriptionRepository.unsubscribeConversationEvents();
    }

    if (mutation.type === ConversationMutation.SET_UNSUBSCRIBE_ALL && state.conversation.unsubscribeAll) {
      console.log("KtznEventSubscriptionPlugin unsubscribeAll() called");
      this._ktznEventSubscriptionRepository.unsubscribeFromAllEvents();
      store.commit(ConversationMutation.SET_HAS_STARTED_APPSYNC_SUBSCRIPTION, false);
    }

    if (mutation.type === ConversationMutation.SET_CONVERSATION_IDS) {
      for (const conversationId of state.conversation.conversationIds) {
        this._ktznEventSubscriptionRepository.subscribeToEvent(`conversation/${conversationId}`);
      }
    }

    if (mutation.type === ConversationMutation.CLEAR_CONVERSATION_IDS) {
      this._ktznEventSubscriptionRepository.unsubscribeConversationEvents();
    }

    if (mutation.type === ConversationMutation.ADD_CONVERSATION_ID) {
      this._ktznEventSubscriptionRepository.subscribeToEvent(`conversation/${mutation.payload}`);
    }
  };
}

export default new StoreSubscriptionService();
